// Shared GrowthBook client. Lives outside App.jsx so non-React callers
// (utils/everflow, which runs at boot) can await the same init without pulling
// the whole app in — utils/paywall imports utils/everflow, so an import back
// into App.jsx would risk a cycle.
//
// Moved here from Root() unchanged: same options, same `streaming: true`, the
// same attribute layering and the same country-lookup overlap. Two things that
// were incidental to living inside a component are now fixed by construction:
// the instance is built once instead of on every render, and init runs at boot
// rather than in an effect, so a boot-time caller has something to await.

import { GrowthBook } from '@growthbook/growthbook'
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins'
import {
  ensureGbCountryCode,
  getGbDeviceId,
  getGbGeoAttributes,
} from '../utils/gbIdentity'

const GB_BASE_URL = process.env.REACT_APP_GB_API
const GB_BASE_KEY = process.env.REACT_APP_GB_KEY

// Moved with the init block it feeds — it had no other caller in App.jsx.
function getUTMParams() {
  const params = new URLSearchParams(window.location.search)
  const utm = {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    cmpid: params.get('cmpid'),
    pubid: params.get('pubid'),
    prelandername: params.get('prelandername'),
  }

  const hasUtm = Object.values(utm).some(Boolean)

  if (hasUtm) {
    // localStorage.setItem('utm_params', JSON.stringify(utm));
    return utm
  }

  const stored = localStorage.getItem('utm_params')
  return stored ? JSON.parse(stored) : {}
}

export const growthbook = new GrowthBook({
  apiHost: GB_BASE_URL || 'https://cdn.growthbook.io',
  clientKey: GB_BASE_KEY || '',
  enableDevMode: process.env.NODE_ENV === 'development',
  trackingCallback: (experiment, result) => {
    console.log('Viewed Experiment', {
      experimentId: experiment.key,
      variationId: result.key,
    })
  },
  plugins: [autoAttributesPlugin()],
})

// Kicked off alongside init so the round-trip overlaps GrowthBook's own payload
// fetch. Never awaited before init — that would stall every flag behind it.
const countryReady = ensureGbCountryCode()

// Resolves once features are loaded and the attributes are applied. Callers
// needing a deadline must race this themselves — do NOT pass `timeout` to
// init(): the SDK only subscribes to late-arriving feature data when streaming
// is on, so a timed-out init would leave the app with an empty payload.
export const gbReady = growthbook.init({ streaming: true }).then(() => {
  // Persisted GrowthBook hashing id — one identity for the whole funnel,
  // including the Google OAuth round-trip. See utils/gbIdentity.
  const deviceId = getGbDeviceId()
  const utm = getUTMParams()
  growthbook.setAttributes({
    id: deviceId,
    deviceId: deviceId,
    utmSource: utm.utm_source || null,
    utmMedium: utm.utm_medium || null,
    utmCampaign: utm.utm_campaign || null,
    cmpid: utm.cmpid || null,
    pubid: utm.pubid || null,
    prelandername: utm.prelandername || null,
    url: window.location.href,
    variation: 'sfw',
    // Already filled on the OAuth return (cached for the tab); on a first load
    // the updateAttributes below applies it once the lookup lands.
    ...getGbGeoAttributes(),
  })
  // location-based-price-multiplier is country-targeted, so it can only resolve
  // once the geo lookup lands. updateAttributes MERGES (setAttributes replaces)
  // and the React SDK re-renders consumers, so useFeatureValue picks the value up
  // long before any price is printed at checkout.
  countryReady.then((countryCode) => {
    if (countryCode) growthbook.updateAttributes(getGbGeoAttributes())
  })
})
